"use client";

import { useEffect, useState } from "react";
import Link from "next/link";

/**
 * Route-segment error boundary.
 *
 * Next.js shows a generic "Application error" page in production mode —
 * this replaces it with the *real* error message, stack, request ID,
 * URL, and a one-click copy-to-clipboard button so failures can be
 * pasted straight into an issue (or into a chat with Claude).
 */
export default function RouteError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [debug, setDebug] = useState<string>("");

  useEffect(() => {
    // Build the structured report once on mount. Lazy because some of
    // these (location, navigator) are browser-only.
    const payload = {
      message: error.message,
      name: error.name,
      digest: error.digest ?? null,
      stack: error.stack ?? null,
      url: typeof window !== "undefined" ? window.location.href : "",
      userAgent:
        typeof navigator !== "undefined" ? navigator.userAgent : "",
      timestamp: new Date().toISOString(),
    };
    setDebug(JSON.stringify(payload, null, 2));
    // Also log to console so devtools captures it even if the user
    // hasn't expanded the error UI yet.
    console.error("[frontier:route-error]", payload);
  }, [error]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(debug);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Some browsers block clipboard in non-secure contexts; fall back
      // to selecting the textarea so the user can Ctrl+C manually.
      const ta = document.getElementById("error-debug-ta") as HTMLTextAreaElement | null;
      ta?.select();
    }
  }

  return (
    <div className="min-h-[70vh] px-6 py-12">
      <div className="mx-auto max-w-3xl">
        <h1 className="text-2xl font-semibold text-red-700">
          Something went wrong rendering this page
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          A client-side error was caught by the route boundary. The full
          diagnostic payload is below — copy it and paste into the chat
          for fastest triage.
        </p>

        <div className="mt-6 rounded-md border border-red-300 bg-red-50/40 p-4">
          <div className="text-xs uppercase tracking-wider text-red-700">
            Error
          </div>
          <div className="mt-1 font-mono text-sm text-red-900 break-all">
            {error.name}: {error.message}
          </div>
          {error.digest && (
            <div className="mt-2 text-xs text-gray-600">
              digest: <code className="font-mono">{error.digest}</code>
            </div>
          )}
        </div>

        {error.stack && (
          <details className="mt-4" open>
            <summary className="cursor-pointer text-sm font-medium text-gray-700">
              Stack trace
            </summary>
            <pre className="mt-2 max-h-72 overflow-auto rounded-md border border-gray-200 bg-gray-50 p-3 text-xs leading-relaxed text-gray-800">
              {error.stack}
            </pre>
          </details>
        )}

        <details className="mt-4">
          <summary className="cursor-pointer text-sm font-medium text-gray-700">
            Full debug payload (JSON)
          </summary>
          <textarea
            id="error-debug-ta"
            readOnly
            value={debug}
            className="mt-2 h-48 w-full rounded-md border border-gray-200 bg-gray-50 p-3 font-mono text-xs"
          />
        </details>

        <div className="mt-6 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={copy}
            className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
          >
            {copied ? "Copied!" : "Copy debug info"}
          </button>
          <button
            type="button"
            onClick={() => reset()}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium hover:bg-gray-50"
          >
            Try again
          </button>
          <Link
            href="/dashboard"
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium hover:bg-gray-50"
          >
            Back to dashboard
          </Link>
        </div>

        <p className="mt-6 text-xs text-gray-500">
          Tip: open DevTools → Console to see the same payload logged with
          source-map-resolved file paths.
        </p>
      </div>
    </div>
  );
}
